import { datalabWithTokenHttp } from '../common';
import type { IApiResponse } from '../common';

export interface IAiPromptItem {
  id: number;
  uuid: string;
  name?: string;
  name_cn?: string;
  name_en?: string;
  description?: string;
  analysis_context?: string;
  prompt_content?: string;
  scope?: string;
  analysis_mode?: string;
  is_default?: number;
  /** 是否模板：0-否，1-是 */
  is_template?: number;
  /** 模板类型：personal / game / public */
  template_category?: string;
  use_count?: number;
  created_at?: string;
  updated_at?: string;
  /** 模板创建人用户 id（prompts/list 新增） */
  user_id?: string | number;
  /** 模板创建人展示名（prompts/list 新增） */
  user_name?: string;
}

export interface IAiPromptFavoriteItem {
  uuid: string;
  name: string;
  scope: string;
}

export interface IAiPromptListData {
  total: number;
  page: number;
  pagesize: number;
  list: IAiPromptItem[];
  favorites?: IAiPromptFavoriteItem[];
}

export interface IGetAiPromptsParams {
  game_code: string;
  /** 报表 id；新建报表尚未保存时可不传，后端仍返回公共/业务级模板 */
  dashboard_id?: string;
  scope?: string;
  analysis_mode?: string;
  keyword?: string;
}

/** AI 分析思路模板类型，与 prompts/save 接口 template_category 字段对齐 */
export type TAiPromptTemplateCategory = 'personal' | 'game' | 'public';

export interface ISaveAiPromptParams {
  game_code: string;
  dashboard_id: string;
  card_id: string;
  is_custom: number;
  prompt_content: string;
  analysis_context?: string;
  /** 是否模板：0-否，1-是 */
  is_template?: number;
  /** 模板类型：personal（个人）、game（业务）、public（公共）；is_template=1 时传 */
  template_category?: TAiPromptTemplateCategory;
  /**
   * 模板名称（UI 必填），与 prompts/save 接口 name_cn 字段对齐。
   */
  name_cn?: string;
}

export interface ISaveAiPromptData {
  uuid: string;
  is_new?: boolean;
  id?: number;
}

export interface IEditAiPromptParams {
  game_code: string;
  prompt_id: number;
  is_custom: number;
  prompt_content: string;
  analysis_context?: string;
}

/** 用户「下次自动应用」配置项，与 prompts/apply/get 响应 data[] 元素对齐 */
export interface IAiPromptApplyConfigItem {
  id: number;
  card_id: string;
  chart_ids?: string[];
  name?: string;
  prompt_content: string;
  analysis_context?: string;
  updated_at?: string;
}

export interface ISaveAiPromptApplyConfigParams {
  game_code: string;
  dashboard_id: string;
  card_id: string;
  chart_ids?: string[];
  name?: string;
  prompt_content: string;
  analysis_context?: string;
}

export interface ISaveAiPromptApplyConfigData {
  id: number;
  is_new: boolean;
}

export interface IGetAiPromptApplyConfigParams {
  game_code: string;
  dashboard_id: string;
}

/**
 * 作用：获取 AI Prompt 模板列表（用于 AI 分析设置中的“分析思路”下拉与搜索）。
 *
 * used-by:
 * - `src/pages/datalab/components/DashboardLayout/components/AiAnalysis/AiSettings/Settings.tsx`
 *
 * @remarks iframe-token: 跟随 datalab request 封装自动处理
 */
export function getAiPrompts(
  params: IGetAiPromptsParams,
): Promise<IApiResponse<IAiPromptListData>> {
  return datalabWithTokenHttp({
    url: '/api/v1/datalab/ai/prompts/list',
    method: 'post',
    data: params,
  });
}

/**
 * 作用：保存用户自定义 Prompt 或分析模板（存在则更新，不存在则新增）。
 *
 * used-by:
 * - `src/pages/datalab/components/DashboardLayout/components/AiAnalysis/AiSettings/hooks.ts`（卡片自定义 prompt）
 * - `src/pages/datalab/components/DashboardLayout/components/AiAnalysis/AiSettings/index.tsx`（设置模板，is_template=1）
 *
 * @remarks iframe-token: 跟随 datalab request 封装自动处理
 */
export function saveAiPrompt(
  params: ISaveAiPromptParams,
): Promise<ISaveAiPromptData> {
  return datalabWithTokenHttp({
    url: '/api/v1/datalab/ai/prompts/save',
    method: 'post',
    data: params,
  });
}

/**
 * 作用：编辑已有的自定义 Prompt 内容与分析背景。
 *
 * used-by:
 * - `src/pages/datalab/components/DashboardLayout/components/AiAnalysis/AiSettings/hooks.ts`
 *
 * @remarks iframe-token: 跟随 datalab request 封装自动处理
 */
export function editAiPrompt(
  params: IEditAiPromptParams,
): Promise<ISaveAiPromptData> {
  return datalabWithTokenHttp({
    url: '/api/v1/datalab/ai/prompts/edit',
    method: 'post',
    data: params,
  });
}

/**
 * 作用：保存/更新用户在某报表某卡片上的「下次自动应用」配置（按 user + game + dashboard + card 唯一键 upsert）。
 *
 * used-by:
 * - `AiAnalysis/AiSettings/index.tsx`（预览态「下次自动应用」按钮）
 *
 * @remarks iframe-token: 跟随 datalab request 封装自动处理
 */
export function saveAiPromptApplyConfig(
  params: ISaveAiPromptApplyConfigParams,
): Promise<ISaveAiPromptApplyConfigData> {
  return datalabWithTokenHttp({
    url: '/api/v1/datalab/ai/prompts/apply/save',
    method: 'post',
    data: params,
  });
}

/**
 * 作用：获取当前用户在指定报表下所有 AI 分析卡片的「下次自动应用」配置列表。
 *
 * used-by:
 * - `AiAnalysis/hooks/useAiPromptApplyConfigs.ts`
 *
 * @remarks iframe-token: 跟随 datalab request 封装自动处理
 */
export function getAiPromptApplyConfigs(
  params: IGetAiPromptApplyConfigParams,
): Promise<IAiPromptApplyConfigItem[]> {
  return datalabWithTokenHttp({
    url: '/api/v1/datalab/ai/prompts/apply/get',
    method: 'post',
    data: params,
  });
}

